import { IAppTech } from "api/types/_campaign";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import DropdownOption from "components/Common/DropdownOption";
import Hashtag from "components/Common/Hashtag";
import TooltipCustom from "components/Common/TooltipCustom";
import { useFormik } from "formik";
import { APP_TECH_TYPE, APP_TECH_TYPE_OPTIONS } from "helpers/constans";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import 'react-json-pretty/themes/monikai.css';
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import { Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { postAppTech, putAppTech } from "store/campaign/thunk";
import * as Yup from "yup";

export interface Props {
    isOpen?: boolean;
    detail?: IAppTech | null,
    onClose?: () => void
    triggerRefresh?: (type?: string) => void;
}

const FILE_SIZE = 11 * 1024 * 1024; // 11MB
const SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

const CampaignForm = ({
    isOpen = false,
    detail = null,
    onClose,
    triggerRefresh
}: Props) => {
    const { t, i18n } = useTranslation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const APP_TECH_TYPE_OPTIONS_LANG: any = APP_TECH_TYPE_OPTIONS?.map((item: any) => ({ value: item?.value, label: t(item?.label) }));

    const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false);

    const onCloseClick = () => {
        onClose && onClose();
    }

    const handleSubmit = async (values: any) => {
        try {
            setIsLoading((_prev) => true);

            const data = {
                name: values?.name || '',
                keyword: values?.keyword || '',
                // type: Number(values?.type?.value || APP_TECH_TYPE_OPTIONS_LANG[0]?.value),
                ...(
                    (String(values?.type?.value) === APP_TECH_TYPE.AUTOMATIC) ? {
                        collect_keywords: (values?.collect_keywords || [])?.map((k: any) => k?.id),
                        exclude_keywords: (values?.exclude_keywords || [])?.map((k: any) => k?.id),
                    } :
                        {}
                )
            };
            const response: any = !!detail?.id ? await putAppTech(detail?.id, data) : await postAppTech(data);
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                toast(
                    `${t("The process has been completed.")}`,
                    CONFIG_OPTION_TOAST_NORMAL
                );
                triggerRefresh && triggerRefresh();
            } else {
                setIsLoading((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        name: Yup.string().required(`${t("This is required")}`),
        type: Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
        }).nullable(),
        keyword: Yup.string().required(`${t("This is required")}`),
        collect_keywords: Yup.array().of(
            Yup.object().shape({
                id: Yup.string(),
                text: Yup.string(),
            })
        ).nullable(),
        exclude_keywords: Yup.array().of(
            Yup.object().shape({
                id: Yup.string(),
                text: Yup.string(),
            })
        ).nullable(),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            type: null,
            keyword: '',
            collect_keywords: [],
            exclude_keywords: []
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {
                const _name = detail?.name || '';
                const _keyword = detail?.keyword || '';

                const _collect_keywords = (detail?.collect_keywords || [])?.map((k: any) => ({ id: k, text: k }));
                const _exclude_keywords = (detail?.exclude_keywords || [])?.map((k: any) => ({ id: k, text: k }));

                const _type = APP_TECH_TYPE_OPTIONS_LANG?.find((_t: any) => String(_t?.value) === String(detail?.type)) || APP_TECH_TYPE_OPTIONS_LANG[0];

                formik.setFieldValue('name', _name);
                formik.setFieldValue('keyword', _keyword);
                setTimeout(() => {
                    formik.setFieldValue('type', _type);
                    formik.setFieldValue('collect_keywords', _collect_keywords);
                    formik.setFieldValue('exclude_keywords', _exclude_keywords);
                    formik.setErrors({});
                }, 300);
            } catch (error: any) {
                // setIsLoadingDetail((_prev) => false);
                return error;
            }
        };
        handleCallAllOption();
    }, [JSON.stringify(detail)]);

    return (
        <React.Fragment>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="md"
                className={`border-header-footer`}
                scrollable={false}
                toggle={onCloseClick}
                keyboard={true}
            >
                <ModalHeader toggle={onCloseClick} className="modal-title-flex-grow-1">
                    <div className="d-flex flex-column gap-0 gap-sm-2 fs-18 fs-md-20">
                        <div className="flex-grow-1">
                            <>{!!detail?.id ? t('Button Update App') : t('Button Create App')}</>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="position-relative">
                    <div>
                        <form onSubmit={formik.handleSubmit}>
                            <Row>
                                <Col sm={12} className="mb-3">
                                    <Label htmlFor="name" className="form-label">{t('App')} <span className="text-danger"> *</span></Label>
                                    <Input
                                        name="name"
                                        type="text"
                                        className="form-control"
                                        id="name"
                                        placeholder={t('App')}
                                        value={formik?.values?.name}
                                        onChange={(event: any) => formik.setFieldValue('name', event?.target?.value)}
                                    />
                                    {formik.touched.name && formik.errors.name ? (
                                        <div className="text-danger mt-2">{formik.errors.name}</div>
                                    ) : null}
                                </Col>
                                <Col sm={12} className="mb-3">
                                    <Label htmlFor="type" className="form-label">{t('Type')} <span className="text-danger"> *</span></Label>
                                    <DropdownOption
                                        name="type"
                                        dataList={APP_TECH_TYPE_OPTIONS_LANG}
                                        placeholder={`${t("Type")}...`}
                                        className="search-filter-category-type dropdown-status-rounded"
                                        classNamePrefix="name-prefix"
                                        initialValue={formik?.values?.type || APP_TECH_TYPE_OPTIONS_LANG[0]}
                                        onChangeSelect={(e: any) => {
                                            formik.setFieldValue('type', e)
                                        }}
                                        isHasOptionAll={false}
                                        disabled
                                    />
                                    {formik.touched.type && formik.errors.type ? (
                                        <div className="text-danger mt-2">{formik.errors.type}</div>
                                    ) : null}
                                </Col>
                                <Col sm={12} className="mb-3">
                                    <Label htmlFor="keyword" className="form-label">{t('Boosting Keyword')} <span className="text-danger"> *</span></Label>
                                    <Input
                                        name="keyword"
                                        type="text"
                                        className="form-control"
                                        id="keyword"
                                        placeholder={t('Boosting Keyword')}
                                        value={formik?.values?.keyword}
                                        onChange={(event: any) => formik.setFieldValue('keyword', event?.target?.value)}
                                    />
                                    {formik.touched.keyword && formik.errors.keyword ? (
                                        <div className="text-danger mt-2">{formik.errors.keyword}</div>
                                    ) : null}
                                </Col>
                                {(`${(formik?.values?.type as any)?.value}` === APP_TECH_TYPE.AUTOMATIC) && <>
                                    <Col sm={12} md={12} xl={12} className='mt-2 mb-3'>
                                        <div style={{ minHeight: '80px' }}>
                                            <label className="form-label">
                                                {t("Search Keyword")}
                                            </label>
                                            <TooltipCustom
                                                title={t("Keywords used to search for the app’s correct answers in the title when collecting answers from sources.")}
                                                className="d-inline-block vertical-align-middle ms-1"
                                                style={{ transform: 'translateY(1px)' }}
                                            >
                                                <i className="ri-question-line align-bottom text-secondary" ></i>
                                            </TooltipCustom>
                                            <Hashtag
                                                name="collect_keywords"
                                                placeholder={`${t("Search Keyword")}...`}
                                                suggestions={[]}
                                                initialValue={formik?.values?.collect_keywords || []}
                                                onChangeTag={(event: any) => {
                                                    formik.setFieldValue("collect_keywords", event);
                                                }}
                                                idSelected="hashtags-CampaignFormMulti"
                                                maxHashtags={2000}
                                                maxHeight="150px"
                                            />
                                            {formik.touched.collect_keywords && formik.errors.collect_keywords ? (
                                                <div className="text-danger mt-2">{formik.errors.collect_keywords}</div>
                                            ) : null}
                                        </div>
                                    </Col>
                                    <Col sm={12} md={12} xl={12} className='mt-2 mb-3'>
                                        <div style={{ minHeight: '80px' }}>
                                            <label className="form-label">
                                                {t("Exclude Keyword")}
                                            </label>
                                            <TooltipCustom
                                                title={t("If the title contains an exclusion keyword, it will be excluded from collection.")}
                                                className="d-inline-block vertical-align-middle ms-1"
                                                style={{ transform: 'translateY(1px)' }}
                                            >
                                                <i className="ri-question-line align-bottom text-secondary" ></i>
                                            </TooltipCustom>
                                            <Hashtag
                                                name="exclude_keywords"
                                                placeholder={`${t("Exclude Keyword")}...`}
                                                suggestions={[]}
                                                initialValue={formik?.values?.exclude_keywords || []}
                                                onChangeTag={(event: any) => {
                                                    formik.setFieldValue("exclude_keywords", event);
                                                }}
                                                idSelected="hashtags-CampaignFormMulti"
                                                maxHashtags={2000}
                                                maxHeight="150px"
                                            />
                                            {formik.touched.exclude_keywords && formik.errors.exclude_keywords ? (
                                                <div className="text-danger mt-2">{formik.errors.exclude_keywords}</div>
                                            ) : null}
                                        </div>
                                    </Col>
                                </>}
                                <Col sm={12} className="mb-2" >
                                    <div className="d-flex gap-3 justify-content-end align-item-center">
                                        <button
                                            className="btn btn-primary fs-14 px-1 d-flex justify-content-center align-item-center"
                                            type="submit"
                                            style={{ width: '130px' }}
                                            disabled={isLoading}
                                        >
                                            {isLoading && <span style={{ transform: 'translateY(1px)' }} className="me-1"><Spinner size="sm"></Spinner></span>}<span>{
                                                !!detail?.id ? t('Button Update App') : t('Button Create App')
                                            }</span>
                                        </button>
                                        <button
                                            className="btn btn-secondary fs-14 px-1 d-flex justify-content-center align-item-center"
                                            type="button"
                                            onClick={onCloseClick}
                                            style={{ width: '75px' }}
                                        >
                                            <i className="ri-close-fill" style={{ lineHeight: '22px' }} /> <span className="ms-1">{t("Button Close")}</span>
                                        </button>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </div>
                </ModalBody>
            </Modal>
        </React.Fragment >
    );
};

export default CampaignForm;

